import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { requests as seedRequests, type CitizenRequest } from '../data/demandes';
export type NotifyChannel = 'courriel' | 'sms';
interface RequestsContextValue {
  requests: CitizenRequest[];
  /** Ajoute une requête en tête de liste, sans doublon d'identifiant. */
  addRequest: (request: CitizenRequest) => void;
  /** Canaux d'avis choisis par requête (ATNA et fiche de détail partagent ce choix). */
  notifyChannels: Record<string, NotifyChannel[]>;
  toggleNotifyChannel: (requestId: string, channel: NotifyChannel) => void;
  /** Ajoute la réponse de la citoyenne au fil de la requête et lève la « réponse attendue ». */
  replyToRequest: (requestId: string, body: string) => void;
}
const READ_DELAY_MS = 3500;
const RequestsContext = createContext<RequestsContextValue>({
  requests: seedRequests,
  addRequest: () => {},
  notifyChannels: {},
  toggleNotifyChannel: () => {},
  replyToRequest: () => {}
});
export function RequestsProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [requests, setRequests] = useState<CitizenRequest[]>(seedRequests);
  const [notifyChannels, setNotifyChannels] = useState<Record<string, NotifyChannel[]>>({});
  const addRequest = useCallback((request: CitizenRequest) => {
    setRequests(current => current.some(item => item.id === request.id) ? current : [request, ...current]);
  }, []);
  const toggleNotifyChannel = useCallback((requestId: string, channel: NotifyChannel) => {
    setNotifyChannels(current => {
      const selected = current[requestId] ?? [];
      return {
        ...current,
        [requestId]: selected.includes(channel) ? selected.filter(item => item !== channel) : [...selected, channel]
      };
    });
  }, []);
  const replyToRequest = useCallback((requestId: string, body: string) => {
    const messageId = `atna-${Date.now()}`;
    setRequests(current => current.map(request => request.id !== requestId ? request : {
      ...request,
      messages: [...request.messages.map(message => message.awaiting ? {
        ...message,
        awaiting: undefined
      } : message), {
        id: messageId,
        author: 'citoyen',
        authorName: 'Vous',
        sentAt: 'À l’instant',
        body
      }]
    }));
    // Simule la lecture par la Ville quelques secondes plus tard.
    window.setTimeout(() => {
      setRequests(current => current.map(request => request.id !== requestId ? request : {
        ...request,
        messages: request.messages.map(message => message.id === messageId ? {
          ...message,
          readAt: 'À l’instant'
        } : message)
      }));
    }, READ_DELAY_MS);
  }, []);
  const value = useMemo(() => ({
    requests,
    addRequest,
    notifyChannels,
    toggleNotifyChannel,
    replyToRequest
  }), [requests, addRequest, notifyChannels, toggleNotifyChannel, replyToRequest]);
  return <RequestsContext.Provider value={value} data-id="element-2875" data-mp="rdud7o74cb6t">{children}</RequestsContext.Provider>;
}
export function useRequests(): RequestsContextValue {
  return useContext(RequestsContext);
}